import { RouteConfig } from "src/types";

// Please update route config paths with CAUTION:
// Some route paths are hardcoded server-side

export const GENERAL_ROUTES_CONFIG = {
  forbidden: {
    displayName: "Forbidden",
    path: "/forbidden",
    roleRequired: "NONE"
  },
  info: {
    displayName: "Info",
    path: "/info",
    roleRequired: "WHITELISTED"
  }
} satisfies Record<string, RouteConfig>;

export const TRANSFER_ORDERS_ROUTES_CONFIG = {
  createTransferOrder: {
    displayName: "Create",
    path: "/create-transfer-order",
    emailVerificationRequired: true,
    roleRequired: "HEALF_OPERATIONS_TEAM"
  },
  transferOrdersDashboard: {
    displayName: "Transfer Orders",
    path: "/transfer-orders",
    roleRequired: "HEALF_OPERATIONS_TEAM"
  },
  transferOrder: {
    displayName: "Transfer Order",
    path: "/transfer-order",
    roleRequired: "HEALF_OPERATIONS_TEAM"
  }
} satisfies Record<string, RouteConfig>;

export const ACTIVE_ANTS_PURCHASE_ORDERS_ROUTES_CONFIG = {
  activeAntsPurchaseOrdersDashboard: {
    displayName: "Active Ants Purchase Orders",
    path: "/active-ants-purchase-orders",
    roleRequired: "HEALF_OPERATIONS_TEAM"
  },
  activeAntsPurchaseOrder: {
    displayName: "Active Ants Purchase Order",
    path: "/active-ants-purchase-order",
    roleRequired: "HEALF_OPERATIONS_TEAM"
  },
  resolveAAError: {
    displayName: "Resolve Active Ants Error",
    path: "/resolve-aa-error",
    roleRequired: "HEALF_OPERATIONS_TEAM"
  },
  activeAntsReceivingDiscrepancies: {
    displayName: "Active Ants Receiving Discrepancies",
    path: "/active-ants-purchase-orders/receiving-discrepancies",
    roleRequired: "HEALF_TEAM"
  }
} satisfies Record<string, RouteConfig>;

export const ACTIVE_ANTS_WAREHOUSE_CHECK_INS_ROUTES_CONFIG = {
  activeAntsWarehouseCheckInsDashboard: {
    displayName: "Active Ants Warehouse Check-ins",
    path: "/active-ants-warehouse-check-ins",
    roleRequired: "HEALF_TEAM"
  },
  activeAntsWarehouseCheckIn: {
    displayName: "Active Ants Warehouse Check-in",
    path: "/active-ants-warehouse-check-ins/check-in",
    roleRequired: "HEALF_TEAM"
  },
  activeAntsGoodsInExports: {
    displayName: "Active Ants Goods-in Exports",
    path: "/active-ants-goods-in-exports",
    roleRequired: ["HEALF_ACCOUNTANT", "HEALF_OPERATIONS_TEAM"]
  }
} satisfies Record<string, RouteConfig>;

export const PURCHASE_ORDERS_ROUTES_CONFIG = {
  createPurchaseOrder: {
    displayName: "Create",
    path: "/create-purchase-order",
    emailVerificationRequired: true,
    roleRequired: "HEALF_OPERATIONS_TEAM"
  },
  uploadPurchaseOrders: {
    displayName: "Upload",
    path: "/upload-purchase-orders",
    emailVerificationRequired: true,
    roleRequired: "HEALF_OPERATIONS_TEAM"
  },
  sendPurchaseOrder: {
    displayName: "Send",
    path: "/send-purchase-order",
    emailVerificationRequired: true,
    roleRequired: "HEALF_OPERATIONS_TEAM"
  },
  updatePurchaseOrderConfirmation: {
    displayName: "Update Confirmation",
    path: "/update-purchase-order-confirmation",
    roleRequired: ["VENDOR", "HEALF_OPERATIONS_TEAM"]
  },
  reviewPurchaseOrderConfirmation: {
    displayName: "Review Confirmation",
    path: "/review-confirmation",
    roleRequired: ["HEALF_OPERATIONS_TEAM"]
  },
  updatePurchaseOrderInvoice: {
    displayName: "Update Invoice",
    path: "/update-purchase-order-invoice",
    roleRequired: ["HEALF_ACCOUNTANT", "VENDOR", "HEALF_OPERATIONS_TEAM"]
  },
  amendPurchaseOrder: {
    displayName: "Amend",
    path: "/amend-purchase-order",
    roleRequired: "HEALF_OPERATIONS_TEAM"
  },
  amendTransitPurchaseOrder: {
    displayName: "Amend Transit PO",
    path: "/amend-transit-purchase-order",
    roleRequired: "HEALF_OPERATIONS_TEAM"
  },
  updatePurchaseOrderShipmentDetails: {
    displayName: "Update Shipment",
    path: "/update-purchase-order-shipment-details",
    roleRequired: ["HEALF_OPERATIONS_TEAM", "HEALF_ACCOUNTANT", "VENDOR"]
  },
  createChildPurchaseOrder: {
    displayName: "Create Child PO",
    path: "/create-child-purchase-order",
    roleRequired: ["HEALF_OPERATIONS_TEAM"]
  },
  confirmChildPurchaseOrder: {
    displayName: "Confirm Child PO",
    path: "/confirm-child-purchase-order",
    roleRequired: ["HEALF_OPERATIONS_TEAM"]
  },
  purchaseOrdersDashboard: {
    displayName: "Purchase Orders",
    path: "/purchase-orders",
    roleRequired: ["HEALF_TEAM", "VENDOR", "WAREHOUSE"]
  },
  subPurchaseOrdersDashboard: {
    displayName: "Sub Purchase Orders",
    path: "/sub-purchase-orders",
    roleRequired: ["HEALF_OPERATIONS_TEAM", "WAREHOUSE"]
  },
  subPurchaseOrder: {
    displayName: "Sub Purchase Order",
    path: "/sub-purchase-order",
    roleRequired: ["HEALF_OPERATIONS_TEAM", "WAREHOUSE"]
  },
  transitPurchaseOrdersDashboard: {
    displayName: "Transit Purchase Orders",
    path: "/transit-purchase-orders",
    roleRequired: "HEALF_OPERATIONS_TEAM"
  },
  purchaseOrders: {
    displayName: "Purchase Order",
    path: "/purchase-order",
    roleRequired: ["HEALF_TEAM", "VENDOR", "WAREHOUSE", "RELABELLING"]
  },
  resolveGXOError: {
    displayName: "Resolve GXO Error",
    path: "/resolve-gxo-error",
    roleRequired: ["HEALF_OPERATIONS_TEAM"]
  },
  receivingDiscrepancies: {
    displayName: "Receiving Discrepancies",
    path: "/purchase-orders/receiving-discrepancies",
    roleRequired: ["HEALF_TEAM", "WAREHOUSE"]
  },
  parcelHeroTimeline: {
    displayName: "Parcel Hero Timeline",
    path: "/purchase-orders/parcel-hero-timeline",
    roleRequired: ["HEALF_TEAM"]
  }
} satisfies Record<string, RouteConfig>;

export const FINANCE_ROUTES_CONFIG = {
  invoiceExports: {
    displayName: "Invoice Exports",
    path: "/finance/invoice-exports",
    roleRequired: ["HEALF_TEAM"]
  },
  legacyInvoiceExports: {
    displayName: "Legacy Invoice Exports",
    path: "/finance/legacy-invoice-exports",
    roleRequired: ["HEALF_TEAM"]
  },
  hmrcExchangeRates: {
    displayName: "HMRC Exchange Rates",
    path: "/finance/hmrc-exchange-rates",
    roleRequired: ["HEALF_TEAM"]
  }
} satisfies Record<string, RouteConfig>;

export const USERS_ROUTES_CONFIG = {
  userAccountsDashboard: {
    displayName: "User Accounts",
    path: "/management/user-accounts",
    roleRequired: "HEALF_MANAGEMENT_TEAM"
  },
  createUserAccount: {
    displayName: "Create User Account",
    path: "/management/create-user-account",
    roleRequired: "HEALF_MANAGEMENT_TEAM"
  },
  updateUserAccount: {
    displayName: "Update User Account",
    path: "/management/update-user-account",
    roleRequired: "HEALF_MANAGEMENT_TEAM"
  },
  purchaseOrderBarcodeLookup: {
    displayName: "PO Barcode Lookup",
    path: "/management/po-barcode-lookup",
    roleRequired: ["WAREHOUSE", "RELABELLING", "HEALF_OPERATIONS_TEAM"]
  },
  profile: {
    displayName: "Profile",
    path: "/profile",
    roleRequired: "WHITELISTED"
  },
  userEmailVerification: {
    displayName: "Email Verification",
    path: "/verify-email",
    roleRequired: "HEALF_OPERATIONS_TEAM"
  },
  updateUserSettings: {
    displayName: "Settings",
    path: "/settings",
    roleRequired: "HEALF_OPERATIONS_TEAM"
  }
} satisfies Record<string, RouteConfig>;

export const VENDORS_ROUTES_CONFIG = {
  vendorAccountsDashboard: {
    displayName: "Vendors",
    path: "/management/vendors",
    roleRequired: "HEALF_OPERATIONS_TEAM"
  },
  createVendor: {
    displayName: "Create Vendor",
    path: "/management/create-vendor",
    roleRequired: "HEALF_OPERATIONS_TEAM"
  },
  updateVendor: {
    displayName: "Update Vendor",
    path: "/management/update-vendor",
    roleRequired: "HEALF_OPERATIONS_TEAM"
  },
  bulkEmailToVendors: {
    displayName: "Bulk Email to Vendors",
    path: "/management/bulk-email-vendors",
    emailVerificationRequired: true,
    roleRequired: ["HEALF_OPERATIONS_TEAM", "HEALF_MANAGEMENT_TEAM"]
  }
} satisfies Record<string, RouteConfig>;

export const WAREHOUSE_CHECK_INS_ROUTES_CONFIG = {
  checkInUnknownArrival: {
    displayName: "Check-in Unknown Arrival",
    path: "/check-in-unknown-arrival",
    roleRequired: ["HEALF_OPERATIONS_TEAM"]
  },
  checkInPurchaseOrder: {
    displayName: "Check-in Purchase Order",
    path: "/check-in-purchase-order",
    roleRequired: ["HEALF_OPERATIONS_TEAM", "WAREHOUSE"]
  },
  warehouseCheckInsDashboard: {
    displayName: "Warehouse Check-ins",
    path: "/warehouse-check-ins",
    roleRequired: ["HEALF_TEAM", "WAREHOUSE"]
  },
  warehouseCheckIns: {
    displayName: "Warehouse Check-in",
    path: "/warehouse-check-ins/warehouse-check-in",
    roleRequired: ["HEALF_TEAM", "WAREHOUSE"]
  },
  goodsInExports: {
    displayName: "Goods-in Exports",
    path: "/goods-in-exports",
    roleRequired: ["HEALF_ACCOUNTANT", "HEALF_OPERATIONS_TEAM"]
  },
  unknownArrivals: {
    displayName: "Unknown Arrivals",
    path: "/warehouse-check-ins/unknown-arrivals",
    roleRequired: ["HEALF_TEAM", "WAREHOUSE"]
  }
} satisfies Record<string, RouteConfig>;

export const CLAIMS_ROUTES_CONFIG = {
  claimsDashboard: {
    displayName: "Claims Dashboard",
    path: "/claims",
    roleRequired: ["WAREHOUSE", "HEALF_OPERATIONS_TEAM", "VENDOR"]
  },
  createClaim: {
    displayName: "Create Claim",
    path: "/claims/create",
    roleRequired: ["WAREHOUSE", "HEALF_OPERATIONS_TEAM"]
  },
  editClaim: {
    displayName: "Edit Claim",
    path: "/claims/edit",
    roleRequired: ["HEALF_OPERATIONS_TEAM"]
  },
  claim: {
    displayName: "Claim",
    path: "/claims/claim",
    roleRequired: ["WAREHOUSE", "HEALF_OPERATIONS_TEAM", "VENDOR"]
  },
  reviewClaimConfirmation: {
    displayName: "Review Claim Confirmation",
    path: "/claims/review-confirmation",
    roleRequired: ["VENDOR", "HEALF_OPERATIONS_TEAM"]
  }
} satisfies Record<string, RouteConfig>;

export const RELABELLING_ROUTES_CONFIG = {
  euRelabelCheckIn: {
    displayName: "EU Relabel Check-In",
    path: "/relabelling/eu-relabel-check-in",
    roleRequired: ["RELABELLING", "HEALF_OPERATIONS_TEAM"]
  },
  relabelCaseBuilder: {
    displayName: "Relabel Case Builder",
    path: "/relabelling/relabel-case-builder",
    roleRequired: ["RELABELLING", "HEALF_OPERATIONS_TEAM"]
  },
  relabelCaseTracking: {
    displayName: "Relabel Case Tracking",
    path: "/relabelling/relabel-case-tracking",
    roleRequired: ["RELABELLING", "HEALF_OPERATIONS_TEAM"]
  },
  palletBuilder: {
    displayName: "Pallet Builder",
    path: "/relabelling/pallet-builder",
    roleRequired: ["RELABELLING", "HEALF_OPERATIONS_TEAM"]
  },
  createPallet: {
    displayName: "Create Pallet (Admin)",
    path: "/relabelling/create-pallet",
    roleRequired: ["HEALF_OPERATIONS_TEAM"]
  },
  viewPallet: {
    displayName: "View Pallet",
    path: "/relabelling/view-pallet",
    roleRequired: ["RELABELLING", "HEALF_OPERATIONS_TEAM"]
  },
  editPallet: {
    displayName: "Edit Pallet",
    path: "/relabelling/edit-pallet",
    roleRequired: ["RELABELLING", "HEALF_OPERATIONS_TEAM"]
  },
  shipmentManager: {
    displayName: "Shipment Manager",
    path: "/relabelling/shipment-manager",
    roleRequired: ["RELABELLING", "HEALF_OPERATIONS_TEAM"]
  },
  createShipment: {
    displayName: "Create Shipment",
    path: "/relabelling/create-shipment",
    roleRequired: ["RELABELLING", "HEALF_OPERATIONS_TEAM"]
  },
  editShipment: {
    displayName: "Edit Shipment",
    path: "/relabelling/edit-shipment",
    roleRequired: ["RELABELLING", "HEALF_OPERATIONS_TEAM"]
  },
  viewShipment: {
    displayName: "View Shipment",
    path: "/relabelling/view-shipment",
    roleRequired: ["RELABELLING", "HEALF_OPERATIONS_TEAM"]
  }
} satisfies Record<string, RouteConfig>;

export const BRAND_ROUTES_CONFIG = {
  brandOnboarding: {
    displayName: "Brand Onboarding",
    path: "/brand-onboarding",
    roleRequired: "NONE" as const,
    isPublic: true
  },
  brandSubmissionStatus: {
    displayName: "Brand Submission Status",
    path: "/brand-onboarding/status/:token",
    roleRequired: "NONE" as const,
    isPublic: true
  },
  brandSubmissionResubmit: {
    displayName: "Brand Submission Resubmit",
    path: "/brand-onboarding/resubmit/:token",
    roleRequired: "NONE" as const,
    isPublic: true
  },
  brandSubmissions: {
    displayName: "Brand Submissions",
    path: "/brand-submissions",
    roleRequired: ["HEALF_TEAM", "VENDOR"]
  },
  brandSubmissionDetail: {
    displayName: "Brand Submission",
    path: "/brand-submissions/:id",
    roleRequired: ["HEALF_TEAM", "VENDOR"]
  }
} satisfies Record<string, RouteConfig>;

export const ROUTES_CONFIG: Record<string, RouteConfig> = {
  ...GENERAL_ROUTES_CONFIG,
  ...PURCHASE_ORDERS_ROUTES_CONFIG,
  ...TRANSFER_ORDERS_ROUTES_CONFIG,
  ...ACTIVE_ANTS_PURCHASE_ORDERS_ROUTES_CONFIG,
  ...ACTIVE_ANTS_WAREHOUSE_CHECK_INS_ROUTES_CONFIG,
  ...FINANCE_ROUTES_CONFIG,
  ...USERS_ROUTES_CONFIG,
  ...VENDORS_ROUTES_CONFIG,
  ...WAREHOUSE_CHECK_INS_ROUTES_CONFIG,
  ...CLAIMS_ROUTES_CONFIG,
  ...RELABELLING_ROUTES_CONFIG,
  ...BRAND_ROUTES_CONFIG
};

ROUTES_CONFIG.home = { displayName: "Home", path: "/", roleRequired: "WHITELISTED" };

export default ROUTES_CONFIG;
